import { useCallback, useMemo } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import EventListItemDetails from 'modules/event-list/item/details/index.js';
import GenericMarketContent from 'modules/aggregated-market-pane/generic/index.js';
import { useMarketItems, useEventItem, useEventsLoadDone } from 'modules/common/hooks';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';
import EventCardsTypes from 'modules/event-cards/types.js';
import CardBackground from 'modules/event-cards/background/index.js';
import GameLine from 'modules/event-cards/match/common/game-line.js';
const { EventTimingStatus, FlexPane, Text, Button, SportAndCountryIcon, SportWatermarkImageTypes } =
    MojitoPresentation.Components;
const Scoreboard = MojitoPresentation.Components.Scoreboard.default;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const EventsUtils = MojitoServices.SportsContent.Events.utils;
const { types: MarketGroupTypes } = MojitoServices.SportsContent.MarketGroups;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;
const { createAggregatedMarket } = EventsUtils;
const { AGGREGATED_MARKET_TYPE } = MarketGroupTypes;

const MatchEventCardClassic = withEventCardSkeleton(
    props => {
        const {
            mojitoTools,
            eventId,
            marketId,
            gameLine,
            colorMode,
            shouldRequestData,
            elementRef,
        } = props;
        const { config, style, appContext, stringResolver } = mojitoTools;
        const isGameLineCard = !!gameLine;

        const event = useEventItem(eventId, shouldRequestData);
        const onEventClick = useCallback(() => {
            IntentActionTrigger.showSportEvent(event.sportId, event.id);
            appContext.analyticsEmitter.emitAnalytics('eventCardClicked', event.canonicalName);
        }, [event?.sportId, event?.id, event?.canonicalName, appContext.analyticsEmitter]);
        const isThreeParticipantsEvent = EventsUtils.isThreeParticipantsEvent(event);
        const mode = colorMode || config.mode;
        const contentConfig = useMemo(() => {
            const modeStyle =
                mode === EventCardsTypes.COLOR_MODES.DARK
                    ? style.eventCardDetailsDark
                    : style.eventCardDetails;
            return {
                ...modeStyle,
                eventItemDetails: isThreeParticipantsEvent
                    ? modeStyle.eventItemDetailsForThreeParticipants
                    : modeStyle.eventItemDetails,
            };
        }, [style, mode, isThreeParticipantsEvent]);

        if (!event) {
            return null;
        }

        const altText =
            appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');
        const showBackground =
            props.imgUrl ||
            (config.showBackgroundSportImage &&
                !config.noBackgroundImageSports.includes(event.sportId));
        const isScoreboardEnabled = config.scoreboardEnabledSports.includes(event.sportId);

        return (
            <div style={style.cardWrapper} ref={elementRef}>
                <FlexPane class="ta-MatchEventCard" config={contentConfig.eventCard.container}>
                    {showBackground && (
                        <CardBackground
                            config={config.cardBackground}
                            sportId={event.sportId}
                            imgUrl={props.imgUrl}
                            position={
                                isGameLineCard
                                    ? SportWatermarkImageTypes.POSITIONS.BOTTOM_LEFT
                                    : SportWatermarkImageTypes.POSITIONS.TOP_RIGHT
                            }
                        />
                    )}
                    <Button
                        class="ta-clickableArea"
                        onClick={onEventClick}
                        config={contentConfig.eventCard.clickableArea}
                    >
                        <FlexPane class="ta-headerContainer" config={config.headerContainer}>
                            <SportAndCountryIcon
                                sportId={event.sportId}
                                countryCode={event.countryCode}
                                countryName={event.countryName}
                                alt={altText}
                                config={contentConfig.sportCountryIcons}
                            />
                            <Text class="ta-headerTitle" config={contentConfig.eventCard.title}>
                                {event.typeName}
                            </Text>
                            <EventTimingStatus
                                config={contentConfig.headerTime}
                                eventItem={event}
                                hasStreaming={VideoStreamsUtils.isStreamAvailable(event)}
                                hasClockDetails={EventsUtils.hasInPlayClockDetails(event)}
                                inplayTime={EventsUtils.getEventInplayTime(event)}
                            />
                        </FlexPane>
                    </Button>
                    <FlexPane config={config.participantsAndMarketsContainer}>
                        {isGameLineCard ? (
                            <GameLine
                                {...props}
                                config={contentConfig.gameLineMarketContent}
                                event={event}
                                onEventClick={onEventClick}
                            />
                        ) : (
                            <>
                                {isScoreboardEnabled ? (
                                    <Button
                                        class="ta-clickableArea"
                                        onClick={onEventClick}
                                        config={contentConfig.eventCard.clickableArea}
                                    >
                                        <Scoreboard
                                            event={event}
                                            config={contentConfig.scoreboard}
                                            isOutright={EventsUtils.isOutrightEventType(
                                                event.eventType
                                            )}
                                        />
                                    </Button>
                                ) : (
                                    <EventListItemDetails
                                        hrefLink={NavigationPathBuilder.sportEventPath(
                                            event.sportId,
                                            event.id,
                                            appContext.routeResolver
                                        )}
                                        config={contentConfig.eventItemDetails}
                                        event={event}
                                        cbClick={onEventClick}
                                    />
                                )}
                                <RegularMarket
                                    {...props}
                                    contentConfig={contentConfig}
                                    event={event}
                                    marketId={marketId}
                                />
                            </>
                        )}
                    </FlexPane>
                </FlexPane>
            </div>
        );
    },
    props => useEventsLoadDone([props.eventId]),
    config => config.eventCardDetails.eventCard.skeletonLoader
);

const RegularMarket = props => {
    const { event, marketName, marketId, contentConfig } = props;
    const marketIds = useMemo(() => [marketId], [marketId]);
    const markets = useMarketItems(marketIds);
    const infoMarkets = marketIds.map(id => ({ id }));
    const marketInfo = { type: AGGREGATED_MARKET_TYPE.GENERIC, markets: infoMarkets };
    return (
        <>
            <Text class="ta-marketTitle" config={contentConfig.marketTitle}>
                {marketName}
            </Text>
            <GenericMarketContent
                config={contentConfig.genericMarketContent}
                aggregatedMarket={createAggregatedMarket(marketInfo, markets)}
                eventItem={event}
                showSelectionHeaders={false}
            />
        </>
    );
};

MatchEventCardClassic.getStyle = (config, mode, merge) => {
    const eventCardDetailsDarkConfig = config.eventCardDetailsDark || {};
    const eventCardDetails = merge(config.eventCardDetails, {
        eventItemDetailsForThreeParticipants: merge(
            config.eventCardDetails.eventItemDetails,
            config.eventCardDetails.eventItemDetailsForThreeParticipants
        ),
    });
    const eventCardDetailsDark = merge(eventCardDetails, eventCardDetailsDarkConfig);
    eventCardDetailsDark.eventItemDetailsForThreeParticipants = merge(
        merge(
            eventCardDetails.eventItemDetailsForThreeParticipants,
            eventCardDetailsDarkConfig.eventItemDetails || {}
        ),
        eventCardDetailsDarkConfig.eventItemDetailsForThreeParticipants || {}
    );

    return {
        cardWrapper: {
            position: 'relative',
        },
        eventCardDetails,
        eventCardDetailsDark,
    };
};

export default MatchEventCardClassic;
